import { TRACK_CART_SOURCE } from '~/constants/rudderstack';

const GTM_EVENT_NAME = {
  CART_OPENED: 'cartOpen',
  PRODUCT_ADDED: 'EEcartAdd',
  PRODUCT_REMOVED: 'EEcartRemove',
  PRODUCT_VIEWED: 'EEproductView',
  PRODUCTS_SEARCHED: 'storeSearch',
};

/**
 * Transform the product object from the Shopify app to the format expected by the GTM data layer
 *
 * @param {Object} product - The product object
 * @param {string} sizeSelected - The size of the variant
 * @returns {Object} - The transformed product object
 */
const _transformShopifyProductForGtm = (product, sizeSelected) => {
  let sku = Object.values(product?.sizes)[0]?.sku;
  let size = Object.values(product?.sizes)[0]?.title;
  if (sizeSelected) {
    size = sizeSelected;
    sku = Object.values(product?.sizes).find((s) => s.title === sizeSelected)?.sku || sku;
  }
  const price = (product?.price / 100).toFixed(0);

  return {
    category: product?.type,
    dimension1: size,
    dimension2: decodeURIComponent(product?.colorName),
    dimension18: product?.id,
    dimension21: product?.priceTier,
    dimension22: price,
    id: sku,
    name: decodeURIComponent(product?.masterName),
    price,
    variant: `${size} / ${decodeURIComponent(product?.colorName)}`,
  };
};

/**
 * Transform the product object from the Vue app to the format expected by the GTM data layer
 *
 * @param {Object} product - The product object
 * @returns {Object} - The transformed product object
 */
const _transformAjaxProductForGtm = (product) => {
  const price = `${(product?.price / 100).toFixed(0)}`;
  const sku = product?.sku;
  const size = product?.variant_title;
  const colorName = product?.properties?._color_name;
  return {
    category: product.product_type,
    dimension1: size,
    dimension2: product.properties?._color_name,
    dimension18: product.id,
    dimension21: product.priceTier || 'msrp',
    dimension22: price,
    id: sku,
    name: product.properties?._master_name,
    price,
    variant: `${size} / ${colorName}`,
  };
};

/**
 * Returns the correct transformed product function based on the source
 *
 * - If the source is CART, UPSELL or QUICK_ADD, the product is from AJAX Cart API
 * - If the source is PDP, the product is from Shopify liquid product
 *
 * @param {string} source - The source of the event - This will affect the product transformer function selected
 *
 * @returns {Function} - The transformed product object
 */
const _transformProduct = (source) =>
  source === TRACK_CART_SOURCE.CART ||
  source === TRACK_CART_SOURCE.UPSELL ||
  source === TRACK_CART_SOURCE.QUICK_ADD
    ? _transformAjaxProductForGtm
    : _transformShopifyProductForGtm;

/**
 * Send the product viewed event to GTM
 *
 * @param {Object} product - The product object
 * @param {string} sizeSelected - The size of the variant
 */
const gtmProductViewed = (product, sizeSelected) => {
  window.dataLayer = window.dataLayer || [];
  const productData = _transformShopifyProductForGtm(product, sizeSelected);
  window.dataLayer.push({
    productHandle: product.handle,
    productId: product.id,
    productImagesCf: product.images,
    productName: product.master,
    productPrice: productData.price,
    productSku: productData.id, // This is the SKU
  });
  window.dataLayer.push({
    ecommerce: {
      detail: {
        products: [productData],
      },
    },
    event: GTM_EVENT_NAME.PRODUCT_VIEWED,
  });
};

/**
 * Send the product removed event to GTM
 *
 * @param {Object} productRemoved - The product object
 * @param {Object} cart - The cart object
 */
const gtmProductRemoved = (productRemoved, cart) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ecommerce: {
      cartLineItems: cart?.items?.map(_transformAjaxProductForGtm),
      currencyCode: cart?.currency,
      remove: {
        products: [_transformAjaxProductForGtm(productRemoved)],
      },
    },
    event: GTM_EVENT_NAME.PRODUCT_REMOVED,
  });
};

/**
 * Send the product added event to GTM
 *
 * @param {Object} productToUpdate - The product object
 * @param {Object} cart - The cart object
 * @param {string} source - The source of the event
 */
const gtmProductAdded = (productToUpdate, cart, source) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ecommerce: {
      add: {
        products: [_transformProduct(source)(productToUpdate)],
      },
      cartLineItems: cart?.items?.map(_transformAjaxProductForGtm),
      currencyCode: cart?.currency,
    },
    event: GTM_EVENT_NAME.PRODUCT_ADDED,
  });
};

const gtmCartViewed = (items) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    ecommerce: {
      cartLineItems: items.map(_transformAjaxProductForGtm),
    },
    event: GTM_EVENT_NAME.CART_OPENED,
  });
};

const gtmProductsSearched = ({ query, searchResultsCount, searchResultsItems }) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: GTM_EVENT_NAME.PRODUCTS_SEARCHED,
    searchData: {
      query,
      searchResultsCount,
      searchResultsItems,
    },
  });
};

export { gtmCartViewed, gtmProductAdded, gtmProductRemoved, gtmProductsSearched, gtmProductViewed };
